'use client';

import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, Sparkles } from 'lucide-react';
import type { BreakdownRow, TimePoint, BreakdownInsight, Severity } from '@/lib/ads-types';

// ---- shared formatting ----
export function fmtNum(n: number): string {
  return Math.round(n).toLocaleString();
}
export function fmtMoney(n: number, cur: string): string {
  return `${cur ? cur + ' ' : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

let _regionDN: Intl.DisplayNames | undefined;
try { _regionDN = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { /* noop */ }
function countryName(code: string): string {
  try { return _regionDN?.of(code) || code; } catch { return code; }
}
const labelize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ---- smart insights ----
const SEV: Record<Severity, { icon: typeof Info; color: string; border: string }> = {
  critical: { icon: AlertOctagon, color: 'text-red-400', border: 'border-l-red-500' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', border: 'border-l-amber-500' },
  info: { icon: Info, color: 'text-blue-400', border: 'border-l-blue-500' },
  positive: { icon: CheckCircle2, color: 'text-green-400', border: 'border-l-green-500' },
};

export function SmartInsights({ insights }: { insights: BreakdownInsight[] }) {
  if (!insights?.length) return null;
  return (
    <div className="glass-card p-5">
      <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-cyan-400" /> Smart insights
        <span className="text-gray-600 font-normal">· from breakdowns</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {insights.map((f, i) => {
          const s = SEV[f.severity]; const Icon = s.icon;
          return (
            <div key={i} className={`border-l-2 ${s.border} bg-white/[0.02] rounded-r-lg pl-3 pr-3 py-2`}>
              <div className="flex items-center gap-2 mb-0.5">
                <Icon className={`w-3.5 h-3.5 ${s.color} shrink-0`} />
                <span className="text-sm text-gray-200 font-medium">{f.title}</span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">{f.detail}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- trend charts ----
const axisProps = { stroke: '#4b5563', fontSize: 10, tickLine: false } as const;
const tooltipStyle = { background: 'rgb(17 24 39)', border: '1px solid rgb(55 65 81)', borderRadius: 8, fontSize: 12 };

function shortDate(d: string): string {
  const parts = d.split('-');
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d;
}

export function TrendCharts({ data, currency }: { data: TimePoint[]; currency: string }) {
  if (!data?.length) {
    return <p className="text-sm text-gray-500">No time-series data for this period.</p>;
  }
  const chartData = data.map(d => ({ ...d, label: shortDate(d.date) }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard title={`Daily spend (${currency})`}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="gSpend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#9ca3af' }} />
            <Area type="monotone" dataKey="spend" stroke="#22d3ee" strokeWidth={2} fill="url(#gSpend)" name="Spend" />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Daily clicks & impressions">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="gImpr" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#9ca3af' }} />
            <Area type="monotone" dataKey="impressions" stroke="#60a5fa" strokeWidth={2} fill="url(#gImpr)" name="Impr." />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Daily CTR (%)">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#9ca3af' }} />
            <Line type="monotone" dataKey="ctr" stroke="#34d399" strokeWidth={2} dot={false} name="CTR %" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title={`Daily CPC (${currency})`}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
            <XAxis dataKey="label" {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#9ca3af' }} />
            <Line type="monotone" dataKey="cpc" stroke="#fbbf24" strokeWidth={2} dot={false} name="CPC" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-card p-4">
      <p className="text-xs font-medium text-gray-400 mb-3">{title}</p>
      {children}
    </div>
  );
}

// ---- breakdown panel (segment table with spend-share bars) ----
export function BreakdownPanel({
  title, rows, currency, kind = 'plain', max,
}: {
  title: string;
  rows: BreakdownRow[];
  currency: string;
  kind?: 'plain' | 'country';
  max?: number;
}) {
  if (!rows?.length) {
    return (
      <div className="glass-card p-5">
        <h3 className="text-sm font-semibold text-white mb-1">{title}</h3>
        <p className="text-xs text-gray-500">No data for this breakdown.</p>
      </div>
    );
  }
  const shown = max ? rows.slice(0, max) : rows;
  const maxShare = Math.max(...rows.map(r => r.spendShare || 0), 1);
  const display = (k: string) => (kind === 'country' ? countryName(k) : labelize(k));

  return (
    <div className="glass-card overflow-hidden">
      <div className="px-4 py-3 border-b border-white/[0.04] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <span className="text-[10px] text-gray-500">{rows.length}{max && rows.length > max ? ` · top ${max}` : ''}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-gray-500 uppercase">
              <th className="text-left font-medium px-4 py-2">Segment</th>
              <th className="text-right font-medium px-3 py-2">Spend</th>
              <th className="text-right font-medium px-3 py-2">Share</th>
              <th className="text-right font-medium px-3 py-2">Impr.</th>
              <th className="text-right font-medium px-3 py-2">CTR</th>
              <th className="text-right font-medium px-4 py-2">CPC</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.key} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                <td className="px-4 py-2 text-gray-200 max-w-[180px] truncate" title={display(r.key)}>{display(r.key)}</td>
                <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap">{fmtMoney(r.spend, currency)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-800 overflow-hidden">
                      <div className="h-full bg-cyan-500/70" style={{ width: `${((r.spendShare || 0) / maxShare) * 100}%` }} />
                    </div>
                    <span className="text-gray-400 text-xs w-9 text-right">{r.spendShare ?? 0}%</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-gray-400">{fmtNum(r.impressions)}</td>
                <td className="px-3 py-2 text-right text-cyan-400 font-medium">{r.ctr.toFixed(2)}%</td>
                <td className="px-4 py-2 text-right text-gray-300">{r.cpc > 0 ? fmtMoney(r.cpc, currency) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
